import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EnhancedVAD, WavRecorder, WavStreamPlayer } from '@/lib/wavtools';
import {
  MIN_DURATION_SECS,
  SILENCE_DURATION_MS,
  SLACK_ID_WHITELIST,
} from '@/utils/constants';
import { SessionPhase } from '../components/ui/scenarioinfo';

export const log = (event: string, data?: any) => {
  if (import.meta.env.DEV) {
    console.log(`[${new Date().toISOString()}] ${event}`, data || '');
  }
};

// Constants for timing and controls
const CONSTANTS = {
  MIN_INTERRUPTION_DURATION_MS: 500, // Minimum duration for valid interruption
  INTRO_AUTO_COMPLETE_TIMEOUT_MS: 15000, // 15 seconds max for intro
  INTERRUPTION_WORDS: ['hello', 'hey', 'hi', 'excuse me', 'stop'], // Words that can trigger interruption
  DEBUG: import.meta.env.DEV,
  SPEAKING_DEBOUNCE_MS: 100,
};

const MAX_RETRY_ATTEMPTS = 3;

export const useRolePlay = ({
  sessionId,
  rolePlaySessionRef,
  startTimeRef,
  userSlackId = undefined,
  setSessionPhase,
  setError,
  setIsAISpeaking,
  setIsIntroCompleted,
  endSession,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [items, setItems] = useState<ItemType[]>([]);

  const [isConnected, setIsConnected] = useState(false);
  const [isBadRequest, setIsBadRequest] = useState(false);
  const isIntroCompletedRef = useRef(false);
  const activeAudioTracksRef = useRef(new Set());

  const introResponseIdRef = useRef<string | null>(null);
  const isIntroMessageCompleteRef = useRef(false);
  const introTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTranscriptRef = useRef<string>('');
  const itemsRef = useRef(items);
  const isSkipEnabledRef = useRef(false);
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const practiceStartedRef = useRef(false);

  const wavRecorderRef = useRef<any>(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef<any>(
    new WavStreamPlayer({ sampleRate: 24000 }),
  );
  const clientRef = useRef<RealtimeClient | null>(null);

  const vad = new EnhancedVAD();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupResources();
    };
  }, []);

  const cleanupResources = () => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (wavStreamPlayer) {
      wavStreamPlayer.interrupt();
    }

    const wavRecorder = wavRecorderRef.current;
    if (wavRecorder && wavRecorder.getStatus() !== 'ended') {
      wavRecorder.end();
      wavRecorder.quit();
    }

    if (clientRef.current) {
      clientRef.current.reset();
    }
    if (introTimeoutRef.current) {
      clearTimeout(introTimeoutRef.current);
    }
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
    }
    activeAudioTracksRef.current.clear();
    vad.reset();
    setIsAISpeaking(false);
  };

  useEffect(() => {
    // Nothing to do until practice starts
    if (!practiceStartedRef.current) {
      return;
    }

    // Wait a bit for intro audio stream to start
    setTimeout(() => {
      // Poll for intro completion
      setInterval(() => {
        if (isIntroCompletedRef.current) {
          // Intro already completed, no need to poll
          clearInterval(this);
          return;
        }

        // We can detect intro completion by checking if the player's stream is active (i.e. exists)
        const wavStreamPlayer = wavStreamPlayerRef.current;
        if (
          !isIntroCompletedRef.current && // Intro not yet completed
          wavStreamPlayer && // Player exists (i.e. practice started)
          !wavStreamPlayer.stream // Stream is not active (i.e. intro completed)
        ) {
          console.log('Intro ended, switch to MAIN Conversation');
          clearInterval(this);
          // Wait a bit before switching to main conversation to avoid jarring audio transitions
          setTimeout(() => {
            onSkipIntro();
          }, 1500);
        }
      }, 2000);
    }, 5000);
  }, [practiceStartedRef.current]);

  const updateAISpeakingState = useCallback(() => {
    const isSpeaking = activeAudioTracksRef.current.size > 0;
    log('activeAudioTracksRef.current', activeAudioTracksRef.current);
    setIsAISpeaking(isSpeaking);
    return isSpeaking;
  }, [setIsAISpeaking]);

  const initializeClient = useCallback(() => {
    return new RealtimeClient({
      url: import.meta.env.VITE_SOCKET_URI,
    });
  }, []);

  const connectConversation = useCallback(async () => {
    try {
      log('Connecting Conversation');
      setIsLoading(true);
      setError(null);
      // Ensure skip is disabled when starting new connection
      isSkipEnabledRef.current = false;
      practiceStartedRef.current = true;

      const client = await initializeClient();
      clientRef.current = client;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      startTimeRef.current = Date.now();
      setIsConnected(true);
      setIsBadRequest(false);

      if (wavRecorder.getStatus() !== 'ended') {
        await wavRecorder.end();
      }
      await wavRecorder.begin();
      await wavStreamPlayer.connect();
      await client.connect();

      const isIntro = !isIntroCompletedRef.current;
      log('Configuring Session', { isIntro });
      log('isIntro && isSkipEnabled', {
        isIntro,
        isSkipEnabled: isSkipEnabledRef.current,
      });
      if (isIntro) {
        setSessionPhase(SessionPhase.INTRO);
      } else {
        setSessionPhase(SessionPhase.MAIN_CONVERSATION);
      }

      const config: any = {
        input_audio_transcription: { model: 'whisper-1' },
        turn_detection: isIntro
          ? null
          : {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: SILENCE_DURATION_MS,
            },
        model: 'gpt-4o',
        voice: 'shimmer',
        temperature: 0.8,
      };

      // Session configuration
      client.updateSession(config);

      const sessionData = rolePlaySessionRef.current;
      if (!sessionData) {
        throw new Error('Session data not found');
      }

      const instructions = isIntro
        ? sessionData.voiceInstructions?.intro
        : sessionData.voiceInstructions?.chat;
      let fullInstructions = instructions ?? '';

      if (isIntro) {
        fullInstructions += `
        manager full name: ${sessionData.user.name}
        role-play scenario: ${sessionData.scenario.title}
        role-play description: ${sessionData.scenario.summary}
        role-play objective: ${sessionData.scenario.objective}
        role-play framework: ${sessionData.framework.title}
        This is the introduction phase. Give a clear, complete introduction then wait for the system to transition to the main conversation.
        `;
      } else {
        fullInstructions += `
        manager full name: ${sessionData.user.name}
        this is the main role-play conversation
        `;
        if (sessionData.aiConfig?.initialPrompt) {
          fullInstructions += `
          IMPORTANT: ${sessionData.aiConfig.initialPrompt}
          `;
        }
      }

      log(`Instructions: ${fullInstructions}`);

      client.updateSession({ instructions: fullInstructions });

      // Initial message to start the conversation
      client.sendUserMessageContent([
        {
          type: 'input_text',
          text: isIntro ? 'Please introduce the role-play scenario' : 'Hello',
        },
      ]);

      if (!isIntro && client.getTurnDetectionType() === 'server_vad') {
        await wavRecorder.record((data: any) => {
          if (client.isConnected()) {
            client.appendInputAudio(data.mono);
          }
        });
      }
      startTimeRef.current = Date.now();

      setupClientListeners(client);

      log('Session Configured Successfully');

      if (isIntro) {
        setTimeout(() => {
          isSkipEnabledRef.current = true;
        }, 3000);
      }
    } catch (err: any) {
      log('Connection Error', err);
      setError(err.message || 'Failed to connect to AI Coach');
      setIsConnected(false);
      isSkipEnabledRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update skip intro to use similar cleanup logic
  const onSkipIntro = useCallback(async () => {
    log('manually skipping intro');
    const wavStreamPlayer = wavStreamPlayerRef.current;
    setSessionPhase(SessionPhase.MAIN_CONVERSATION);

    try {
      // 1. Stop current audio playback
      const trackSampleOffset = await wavStreamPlayer.interrupt();

      // 2. If there's an active intro response being played
      if (!isIntroCompletedRef.current && trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await clientRef.current?.cancelResponse(trackId, offset);

        // 3. Wait a small amount of time for audio to fully stop
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (!isIntroCompletedRef.current) {
        // 4. Clean up intro state
        introResponseIdRef.current = null;
        isIntroMessageCompleteRef.current = false;

        // 5. Mark intro as completed
        isIntroCompletedRef.current = true;
        setIsIntroCompleted(true);

        // 7. Start fresh conversation with new audio context
        await connectConversation();
      }
    } catch (err) {
      log('Error during intro skip:', err);
      // Attempt recovery by forcing a fresh connection
      await connectConversation();
    }
  }, [connectConversation]);

  const setupClientListeners = useCallback((client: RealtimeClient) => {
    // Track the intro response ID when it starts
    client.on('server.response.created', ({ response }) => {
      log('Response Created', {
        responseId: response.id,
        isIntro: !isIntroCompletedRef.current,
      });

      if (!isIntroCompletedRef.current) {
        log('Tracking intro response ID', {
          responseId: response.id,
        });
        introResponseIdRef.current = response.id;

        // Set timeout for auto-completion of intro
        introTimeoutRef.current = setTimeout(() => {
          log('!!!! Intro timeout triggered - forcing completion');
          if (!isIntroCompletedRef.current) {
            onSkipIntro();
          }
        }, CONSTANTS.INTRO_AUTO_COMPLETE_TIMEOUT_MS);
      }
    });

    // Handle individual message completion
    client.on('response.output_item.done', ({ item, response_id }) => {
      log('Output Item Done', {
        itemId: item.id,
        responseId: response_id,
        role: item.role,
        isIntroResponse: response_id === introResponseIdRef.current,
      });

      if (item.role === 'assistant') {
        if (
          !isIntroCompletedRef.current &&
          response_id === introResponseIdRef.current
        ) {
          log('Marking intro message as complete');
          isIntroMessageCompleteRef.current = true;
        }
      }
    });

    // Handle complete response completion
    client.on('response.done', async ({ response }) => {
      log('Response Done', {
        responseId: response.id,
        isIntroResponse: response.id === introResponseIdRef.current,
        status: response.status,
      });
    });

    // Handle audio transcription completion
    client.on(
      'conversation.item.input_audio_transcription.completed',
      ({ transcript }) => {
        log('Audio Transcription Completed', { transcript });
        lastTranscriptRef.current = transcript.toLowerCase();
      },
    );

    // Handle conversation interruption with smart detection
    client.on('conversation.interrupted', async () => {
      log('Conversation Interruption Detected');

      try {
        // Check if AI is actually speaking by checking active audio tracks
        if (activeAudioTracksRef.current.size === 0) {
          log('Ignoring interruption - AI not speaking');
          return;
        }

        const { meanValues } = await wavRecorderRef.current.read();
        const isValid = vad.isValidInterruption(meanValues);

        log('Interruption check result:', {
          isValid,
          activeAudioTracks: activeAudioTracksRef.current.size,
          transcript: lastTranscriptRef.current,
        });

        if (!isValid) {
          log('Ignoring interruption - not valid speech detected');
          return;
        }

        const wavStreamPlayer = wavStreamPlayerRef.current;
        const trackSampleOffset = await wavStreamPlayer.interrupt();

        if (trackSampleOffset?.trackId) {
          const { trackId, offset } = trackSampleOffset;
          await client.cancelResponse(trackId, offset);

          // Clear all active tracks
          activeAudioTracksRef.current.clear();
          vad.reset();

          log('Successfully processed interruption', {
            trackId,
            offset,
          });
        }
      } catch (error) {
        log('Error handling interruption:', error);
      }
    });

    // Handle conversation updates
    client.on('conversation.updated', async ({ item, delta }) => {
      const items = client.conversation.getItems();
      log('Conversation Updated', {
        items,
      });
      const wavStreamPlayer = wavStreamPlayerRef.current;
      log('Conversation Updated', {
        item,
        delta,
        introCompleted: isIntroCompletedRef.current,
      });

      if (delta?.audio) {
        const trackId = item.id;
        const isIntro = !isIntroCompletedRef.current;

        try {
          // Ensure track is added to active tracks when audio starts
          activeAudioTracksRef.current.add(trackId);
          updateAISpeakingState();

          log('Starting audio playback:', {
            trackId,
            isIntro,
            activeTracks: Array.from(activeAudioTracksRef.current),
          });

          if (!isIntroCompletedRef.current && !isSkipEnabledRef.current) {
            setTimeout(() => {
              console.log('Enabling skip after delay');
              isSkipEnabledRef.current = true;
            }, 1000);
          }

          wavStreamPlayer.add16BitPCM(delta.audio, trackId, async () => {
            log('Audio chunk completed:', {
              trackId,
              activeTracks: Array.from(activeAudioTracksRef.current),
            });
            activeAudioTracksRef.current.delete(trackId);

            // Wait briefly to allow any queued audio to start playing
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Check if this was the intro message and handle transition
            if (!isIntroCompletedRef.current) {
              log(
                '!!!!Intro audio completed - transitioning to main conversation',
              );

              // Clean up intro state
              if (introTimeoutRef.current) {
                clearTimeout(introTimeoutRef.current);
                introTimeoutRef.current = null;
              }

              try {
                // Mark intro as complete
                isIntroCompletedRef.current = true;
                setIsIntroCompleted(true);
                introResponseIdRef.current = null;
                isIntroMessageCompleteRef.current = false;

                // Start fresh conversation with new audio context
                await connectConversation();

                log('Successfully transitioned to main conversation');
              } catch (error) {
                console.error('Error during intro transition:', error);
                // Attempt recovery by forcing a fresh connection
                await connectConversation();
              }
            }
          });
        } catch (error) {
          console.log('Error processing audio:', error);
          activeAudioTracksRef.current.delete(trackId);
          updateAISpeakingState();
          setError('Audio playback error. Please try again.');
        }
      }

      // Handle completed items (no need to modify isAISpeaking here anymore)
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000,
        );
        item.formatted.file = wavFile;
      }

      setItems(items);
      itemsRef.current = items;
    });

    // Handle errors
    client.on('error', (error: any) => {
      log('Error Occurred', error);
      setError(error.message || 'An unexpected error occurred');
    });
    setItems(client.conversation.getItems());
    itemsRef.current = client.conversation.getItems();
  }, []);

  // Handle retry logic
  const handleRetry = useCallback(() => {
    if (connectionAttempts < MAX_RETRY_ATTEMPTS) {
      connectConversation();
    } else {
      // Reset attempts and try again
      setConnectionAttempts(0);
      connectConversation();
    }
  }, [connectConversation, connectionAttempts]);

  const disconnectConversation = async (closedFeedback = false) => {
    log('disconnectConversation', items);
    setIsConnected(false);
    const client = clientRef.current;
    if (client && client.isConnected()) {
      client.disconnect();
    }
    const wavRecorder = wavRecorderRef.current;
    if (wavRecorder && wavRecorder.getStatus() !== 'ended') {
      await wavRecorder.end();
    }
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (wavStreamPlayer) {
      await wavStreamPlayer.interrupt();
    }

    const duration = (Date.now() - startTimeRef.current) / 1000; // Convert to seconds
    log('Ending session:', { sessionId, duration });

    if (sessionId) {
      if (
        duration < MIN_DURATION_SECS &&
        userSlackId &&
        SLACK_ID_WHITELIST.includes(userSlackId)
      ) {
        setIsBadRequest(true);
        return;
      }

      log('Ending session, items:', items);
      log('Ending session, itemsRef:', itemsRef.current);

      const transcript = (items && items.length > 0 ? items : itemsRef.current)
        .map(
          (item) =>
            `${item.role}: ${item.formatted.text || item.formatted.transcript}`,
        )
        .join('\n');

      log('Ending session:', { sessionId, duration, transcript });
      endSession({
        sessionId,
        transcript,
        duration,
      });
    }
  };

  return {
    disconnectConversation,
    onSkipIntro,
    connectConversation,
    handleRetry,
  };
};
